import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Markdown from "../../site/Markdown";
import { useSeo } from "../../site/ui";
import { LAONA_CATEGORIES } from "../data";

/* 자료실 글쓰기 · 수정 (/laonabot/library/new · /laonabot/library/:id/edit) — 라오나봇 관리자만.
   서버(/api/laona/library)가 관리자 여부를 다시 확인하고, 라오나봇 분류 글만 만들고 고친다.
   본문은 마크다운, 사진은 본문에 바로 넣고, 내려받을 파일은 저장할 때 글에 첨부된다. */

const EMPTY = { category: "", title: "", summary: "", body: "", attachments: [], is_public: true, is_pinned: false };

function toForm(p) {
  return {
    category: p.category || "", title: p.title || "", summary: p.summary || "", body: p.body || "",
    attachments: p.attachments || [], is_public: Boolean(p.is_public), is_pinned: Boolean(p.is_pinned),
  };
}

function formatSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

async function call(path, options = {}) {
  const res = await fetch(`/api/laona/library${path}`, {
    credentials: "same-origin",
    headers: options.body && !(options.body instanceof FormData) ? { "Content-Type": "application/json" } : undefined,
    ...options,
  });
  if (res.status === 204) return null;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(typeof data.detail === "string" ? data.detail : "요청을 처리하지 못했습니다.");
    err.status = res.status;
    throw err;
  }
  return data;
}

/* 큰 파일은 오래 걸리므로 진행률을 보여 주려고 XHR 로 올린다 */
function uploadWithProgress(path, formData, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/laona/library${path}`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      let data = {};
      try { data = JSON.parse(xhr.responseText); } catch { /* 빈 응답 */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new Error(typeof data.detail === "string" ? data.detail : `파일을 올리지 못했습니다. (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("네트워크 오류로 파일을 올리지 못했습니다."));
    xhr.send(formData);
  });
}

/* 클립보드 · 끌어다 놓기에서 사진만 골라낸다 (캡처 도구에 따라 items 에만 들어온다) */
function imageFiles(data) {
  if (!data) return [];
  const files = Array.from(data.files || []);
  if (!files.length && data.items) {
    for (const it of Array.from(data.items)) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
  }
  return files.filter((f) => f.type.startsWith("image/"));
}

/* ---------------------------------------------------------------- 본문 */
function BodyEditor({ value, onChange, busy, onAttach }) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const latest = useRef(value); // 사진을 올리는 동안 이어 쓴 글자가 사라지지 않게 최신 본문을 본다
  latest.current = value;
  const [preview, setPreview] = useState(false);
  const [uploading, setUploading] = useState("");
  const [error, setError] = useState("");

  function wrap(before, after = "", placeholder = "") {
    const ta = ref.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e } = ta;
    const picked = value.slice(s, e) || placeholder;
    onChange(value.slice(0, s) + before + picked + after + value.slice(e));
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(s + before.length, s + before.length + picked.length);
    });
  }

  function linePrefix(prefix) {
    const ta = ref.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e } = ta;
    const start = value.lastIndexOf("\n", s - 1) + 1;
    const block = value.slice(start, e).split("\n").map((l) => prefix + l).join("\n");
    onChange(value.slice(0, start) + block + value.slice(e));
    requestAnimationFrame(() => ta.focus());
  }

  function insertBlock(text) {
    const cur = latest.current;
    const ta = ref.current;
    const pos = ta ? ta.selectionEnd : cur.length;
    const before = cur.slice(0, pos);
    const lead = before && !before.endsWith("\n\n") ? (before.endsWith("\n") ? "\n" : "\n\n") : "";
    onChange(before + lead + text + "\n\n" + cur.slice(pos).replace(/^\n+/, ""));
  }

  async function uploadImages(files) {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    setError("");
    const lines = [];
    try {
      for (let i = 0; i < list.length; i += 1) {
        setUploading(`사진 올리는 중… (${i + 1}/${list.length})`);
        const fd = new FormData();
        fd.append("file", list[i]);
        const res = await call("/images", { method: "POST", body: fd });
        lines.push(`![](${res.url})`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading("");
      if (lines.length) insertBlock(lines.join("\n\n"));
    }
  }

  function onPaste(e) {
    const files = imageFiles(e.clipboardData);
    if (files.length) {
      e.preventDefault();
      uploadImages(files);
    }
  }

  /* 사진은 본문에, 그 밖의 파일(zip · stl 등)은 첨부 파일로 보낸다 */
  function onDrop(e) {
    const all = Array.from(e.dataTransfer?.files || []);
    if (!all.length) return;
    e.preventDefault();
    const images = all.filter((f) => f.type.startsWith("image/"));
    const others = all.filter((f) => !f.type.startsWith("image/"));
    if (images.length) uploadImages(images);
    if (others.length) onAttach(others);
  }

  const disabled = busy || Boolean(uploading) || preview;
  const tools = [
    ["제목", () => linePrefix("## ")],
    ["굵게", () => wrap("**", "**", "굵은 글씨")],
    ["코드", () => wrap("```cpp\n", "\n```", "// 코드")],
    ["목록", () => linePrefix("- ")],
    ["링크", () => wrap("[", "](https://)", "링크 글자")],
    ["표", () => insertBlock("| 항목 | 내용 |\n| --- | --- |\n| | |")],
  ];

  return (
    <div className="lb-field lb-editor__body">
      <span>본문 <i>*</i> <em>— 마크다운. 사진은 '사진 넣기' · 캡처 후 Ctrl+V · 끌어다 놓기로 넣습니다.</em></span>
      <div className="lb-editor__bar">
        {tools.map(([label, fn]) => (
          <button key={label} type="button" className="lb-btn lb-btn--sm" onClick={fn} disabled={disabled}>{label}</button>
        ))}
        <button type="button" className="lb-btn lb-btn--sm lb-btn--primary" onClick={() => fileRef.current?.click()} disabled={disabled}>
          사진 넣기
        </button>
        <input
          ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple hidden
          onChange={(e) => { uploadImages(e.target.files); e.target.value = ""; }}
        />
        <span className="lb-editor__grow" />
        <button type="button" className={`lb-btn lb-btn--sm ${preview ? "lb-btn--primary" : ""}`} onClick={() => setPreview((v) => !v)}>
          {preview ? "작성으로" : "미리보기"}
        </button>
      </div>
      {(uploading || error) && <p className={error ? "lb-error" : "lb-okmsg"}>{error || uploading}</p>}
      {preview ? (
        <div className="lb-editor__preview">
          {value.trim() ? <Markdown source={value} className="lb-post__body" /> : <p className="lb-empty">미리보기할 내용이 없습니다.</p>}
        </div>
      ) : (
        <textarea
          ref={ref}
          className="lb-editor__input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onPaste={onPaste}
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          disabled={busy}
          placeholder={"## 개요\n이 자료에서 다루는 내용을 적어 주세요."}
          spellCheck={false}
        />
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- 화면 */
export default function LaonaEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);
  useSeo({ title: editing ? "자료 수정 — LaonaBot" : "자료 올리기 — LaonaBot", path: "/laonabot/library" });

  const [status, setStatus] = useState("loading"); // loading | ready | denied | missing | error
  const [limits, setLimits] = useState({ max_bytes: 0, allowed_exts: [] });
  const [saved, setSaved] = useState(null);
  const [items, setItems] = useState([]); // 이미 붙은 첨부 파일 정보
  const [form, setForm] = useState(EMPTY);
  const [baseline, setBaseline] = useState(JSON.stringify(EMPTY));
  const [pending, setPending] = useState([]); // 저장할 때 올릴 새 파일
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState("");
  const fileRef = useRef(null);

  function apply(post) {
    setSaved(post);
    setItems(post.attachment_items || []);
    setForm(toForm(post));
    setBaseline(JSON.stringify(toForm(post)));
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const lim = await call("/limits");
        const post = editing ? await call(`/${encodeURIComponent(id)}`) : null;
        if (!alive) return;
        setLimits(lim);
        if (post) apply(post);
        setStatus("ready");
      } catch (err) {
        if (!alive) return;
        setStatus(err.status === 403 ? "denied" : err.status === 404 || err.status === 422 ? "missing" : "error");
      }
    })();
    return () => { alive = false; };
  }, [id, editing]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const dirty = JSON.stringify(form) !== baseline || pending.length > 0;

  function addFiles(list) {
    setError("");
    const ok = [];
    for (const f of Array.from(list || [])) {
      const ext = (f.name.match(/\.[^.]+$/) || [""])[0].toLowerCase();
      if (limits.allowed_exts.length && !limits.allowed_exts.includes(ext)) {
        setError(`올릴 수 없는 파일 형식입니다: ${f.name} — 압축(zip)해서 올려 주세요.`);
        continue;
      }
      if (limits.max_bytes && f.size > limits.max_bytes) {
        setError(`파일이 너무 큽니다: ${f.name} (최대 ${formatSize(limits.max_bytes)})`);
        continue;
      }
      ok.push(f);
    }
    if (ok.length) setPending((prev) => [...prev, ...ok]);
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.category) { setError("분류를 선택해 주세요."); return; }
    if (!form.title.trim()) { setError("제목을 입력해 주세요."); return; }
    if (!form.body.trim()) { setError("본문을 입력해 주세요."); return; }
    setBusy(true); setError("");
    try {
      const payload = JSON.stringify({ ...form, title: form.title.trim() });
      let res = saved
        ? await call(`/${saved.id}`, { method: "POST", body: payload })
        : await call("", { method: "POST", body: payload });
      apply(res);

      /* 새 파일은 글이 저장된 뒤(글 ID 가 생긴 뒤) 하나씩 올린다.
         중간에 실패해도 올라간 파일은 첨부로 남고, 남은 것만 다시 저장하면 된다. */
      const queue = [...pending];
      const total = queue.length;
      while (queue.length > 0) {
        const n = total - queue.length + 1;
        const fd = new FormData();
        fd.append("file", queue[0]);
        fd.append("members_only", "true"); // 라오나봇 자료는 어차피 회원만 받는다 (forum.laona_resource_ids)
        res = await uploadWithProgress(`/${res.id}/files`, fd, (pct) => setProgress(`파일 올리는 중 (${n}/${total}) ${pct}%`));
        apply(res);
        queue.shift();
        setPending([...queue]);
      }
      setProgress("");
      navigate(`/laonabot/library/${res.id}`);
    } catch (err) {
      setError(err.message);
      setProgress("");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!saved || !window.confirm(`'${saved.title}' 자료를 삭제할까요? 이 글에서 올린 첨부 파일도 함께 지워집니다.`)) return;
    setBusy(true); setError("");
    try {
      await call(`/${saved.id}`, { method: "DELETE" });
      navigate("/laonabot/library");
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  function cancel() {
    if (busy) return;
    if (dirty && !window.confirm("저장하지 않은 내용이 있습니다. 저장하지 않고 나갈까요?")) return;
    navigate(saved ? `/laonabot/library/${saved.id}` : "/laonabot/library");
  }

  const byId = Object.fromEntries(items.map((f) => [f.id, f]));

  return (
    <section className="lb-section lb-section--top">
      <div className="lb-wrap lb-post">
        <Link to={saved ? `/laonabot/library/${saved.id}` : "/laonabot/library"} className="lb-back">← 자료실</Link>

        {status === "loading" && <p className="lb-empty">불러오는 중입니다…</p>}
        {status === "error" && <p className="lb-empty">화면을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</p>}
        {status === "denied" && (
          <div className="lb-empty lb-empty--box">
            <b>관리자만 자료를 올릴 수 있습니다</b>
            <p><Link to="/laonabot/library">자료실</Link>로 돌아가 주세요.</p>
          </div>
        )}
        {status === "missing" && (
          <div className="lb-empty lb-empty--box">
            <b>자료를 찾을 수 없습니다</b>
            <p><Link to="/laonabot/library">자료실</Link>로 돌아가 주세요.</p>
          </div>
        )}

        {status === "ready" && (
          <form className="lb-editor" onSubmit={submit}>
            <header className="lb-head">
              <p className="lb-kicker">LIBRARY / {editing ? "EDIT" : "NEW"}</p>
              <h1>{editing ? "자료 수정" : "자료 올리기"}</h1>
            </header>

            <div className="lb-editor__row">
              <label className="lb-field">
                <span>분류 <i>*</i></span>
                <select value={form.category} onChange={upd("category")} disabled={busy}>
                  <option value="" disabled>선택하세요</option>
                  {LAONA_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label} — {c.desc}</option>)}
                </select>
              </label>
              <label className="lb-field lb-editor__title">
                <span>제목 <i>*</i></span>
                <input value={form.title} onChange={upd("title")} disabled={busy} maxLength={200} />
              </label>
            </div>

            <label className="lb-field">
              <span>요약 <em>— 목록 카드에 보이는 한두 문장 · 비우면 본문 앞부분을 씁니다</em></span>
              <textarea rows={2} value={form.summary} onChange={upd("summary")} disabled={busy} />
            </label>

            <BodyEditor
              value={form.body}
              onChange={(body) => setForm((f) => ({ ...f, body }))}
              busy={busy}
              onAttach={addFiles}
            />

            <div className="lb-field">
              <span>첨부 파일 <em>— 자료 아래 '내려받기'에 나옵니다. 본문에 파일을 끌어다 놓아도 여기에 들어옵니다.</em></span>
              {form.attachments.length > 0 || pending.length > 0 ? (
                <ul className="lb-editor__files">
                  {form.attachments.map((aid) => {
                    const f = byId[aid];
                    return (
                      <li key={aid} className="lb-file">
                        <div className="lb-file__main">
                          <b>{f ? f.title : "저장하면 반영됩니다"}</b>
                          {f && !f.missing && (
                            <span className="lb-file__meta">
                              <span className="lb-file__name" title={f.filename}>{f.filename}</span>
                              <span>{formatSize(f.size)}</span>
                              <span>{f.members_only ? "회원 전용" : "누구나"}</span>
                            </span>
                          )}
                        </div>
                        <button
                          type="button" className="lb-btn lb-btn--sm" disabled={busy}
                          onClick={() => setForm((x) => ({ ...x, attachments: x.attachments.filter((v) => v !== aid) }))}
                        >
                          빼기
                        </button>
                      </li>
                    );
                  })}
                  {pending.map((f, i) => (
                    <li key={`${f.name}-${f.size}-${f.lastModified}-${i}`} className="lb-file lb-file--pending">
                      <div className="lb-file__main">
                        <b>{f.name}</b>
                        <span className="lb-file__meta">
                          <span>{formatSize(f.size)}</span>
                          <span>저장할 때 올라갑니다</span>
                        </span>
                      </div>
                      <button type="button" className="lb-btn lb-btn--sm" disabled={busy} onClick={() => setPending((prev) => prev.filter((_, j) => j !== i))}>
                        선택 취소
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="lb-editor__hint">첨부 파일이 없습니다.</p>
              )}
              <div className="lb-editor__bar">
                <button type="button" className="lb-btn lb-btn--primary lb-btn--sm" onClick={() => fileRef.current?.click()} disabled={busy}>+ 파일 추가</button>
                <input ref={fileRef} type="file" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                <span className="lb-editor__hint">최대 {limits.max_bytes ? formatSize(limits.max_bytes) : "…"} · 라오나봇 자료는 회원만 내려받을 수 있습니다</span>
              </div>
            </div>

            <div className="lb-agree">
              <label>
                <input type="checkbox" checked={form.is_public} disabled={busy} onChange={(e) => setForm((f) => ({ ...f, is_public: e.target.checked }))} />
                <span>{form.is_public ? "공개 — 자료실에 바로 보입니다" : "비공개(임시 저장) — 관리자에게만 보입니다"}</span>
              </label>
              <label>
                <input type="checkbox" checked={form.is_pinned} disabled={busy} onChange={(e) => setForm((f) => ({ ...f, is_pinned: e.target.checked }))} />
                <span>공지 — 목록 맨 위에 고정</span>
              </label>
            </div>

            {error && <p className="lb-error">{error}</p>}
            {progress && <p className="lb-okmsg">{progress}</p>}

            <div className="lb-editor__foot">
              {saved && <button type="button" className="lb-btn lb-btn--danger" onClick={remove} disabled={busy}>삭제</button>}
              <span className="lb-editor__grow" />
              <button type="button" className="lb-btn" onClick={cancel} disabled={busy}>취소</button>
              <button type="submit" className="lb-btn lb-btn--primary" disabled={busy}>
                {busy ? "저장 중…" : saved ? "저장" : "올리기"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
